import { useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Trash2, Upload } from "lucide-react";
import { prequalApi, supplierApi, supplierEvalApi, supplierContactApi, supplierDocApi } from "../../api";
import type { SupplierContact, SupplierContactRequest } from "../../api";
import {
  ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection,
} from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";

type Tab = "details" | "contacts" | "documents" | "prequalification" | "evaluations";

const TABS: { id: Tab; label: string }[] = [
  { id: "details",         label: "Details" },
  { id: "contacts",        label: "Contacts" },
  { id: "documents",       label: "Documents" },
  { id: "prequalification",label: "Prequalification" },
  { id: "evaluations",     label: "Evaluations" },
];

const DOC_TYPES = ["TAX_COMPLIANCE", "VAT_CERT", "CR12", "PREQUALIFICATION", "OTHER"] as const;

export default function SupplierViewPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<Tab>("details");

  const { data: supplier, loading, error, reload: reloadSupplier } = useAsyncData(
    () => supplierApi.get(Number(id)),
    [id],
  );

  const { data: prequals } = useAsyncData(
    () => prequalApi.list({ page: 0, size: 20 }),
    [id],
  );

  const { data: evals } = useAsyncData(
    () => supplierEvalApi.list({ page: 0, size: 20 }),
    [id],
  );

  const { data: contacts, reload: reloadContacts } = useAsyncData(
    () => supplierContactApi.list(Number(id)),
    [id],
  );

  const { data: documents, reload: reloadDocs } = useAsyncData(
    () => supplierDocApi.list(Number(id)),
    [id],
  );

  // Activate / Suspend actions
  const { mutate: doActivate, loading: activating, error: activateErr } = useMutation(() =>
    supplierApi.activate(Number(id)).then(() => reloadSupplier()),
  );
  const { mutate: doSuspend, loading: suspending, error: suspendErr } = useMutation(() =>
    supplierApi.suspend(Number(id)).then(() => reloadSupplier()),
  );

  // Contact form state
  const [contactForm, setContactForm] = useState<SupplierContactRequest>({ fullName: "", role: "", phone: "", email: "", primary: false });
  const [savingContact, setSavingContact] = useState(false);
  const [contactErr, setContactErr] = useState<string | null>(null);

  async function saveContact() {
    setSavingContact(true); setContactErr(null);
    try {
      await supplierContactApi.create(Number(id), contactForm);
      setContactForm({ fullName: "", role: "", phone: "", email: "", primary: false });
      reloadContacts();
    } catch (e: unknown) { setContactErr(String((e as { message?: string })?.message ?? e)); }
    setSavingContact(false);
  }

  async function deleteContact(contactId: number) {
    try { await supplierContactApi.delete(Number(id), contactId); reloadContacts(); }
    catch { /* ignore */ }
  }

  // Document upload state
  const fileRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<string>("TAX_COMPLIANCE");
  const [docExpiry, setDocExpiry] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docErr, setDocErr] = useState<string | null>(null);

  async function uploadDoc(file: File) {
    setUploadingDoc(true); setDocErr(null);
    try {
      await supplierDocApi.upload(Number(id), file, docType, docExpiry || undefined);
      reloadDocs();
    } catch (e: unknown) { setDocErr(String((e as { message?: string })?.message ?? e)); }
    setUploadingDoc(false);
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!supplier) return null;

  // Filter to this supplier
  const supplierPrequals = prequals?.content.filter((p) => p.supplierName === supplier.name) ?? [];
  const supplierEvals = evals?.content.filter((e) => e.supplierName === supplier.name) ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/suppliers" className="text-sm text-emerald-700 hover:underline">
          &larr; Back to Suppliers
        </Link>
        <Link
          to={`/procurement/suppliers/${supplier.id}/edit`}
          className="rounded-xl border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50"
        >
          Edit Supplier
        </Link>
      </div>

      {/* Supplier name + status header */}
      <div className="rounded-2xl border-2 border-emerald-700 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Supplier</p>
            <h1 className="mt-0.5 text-xl font-bold text-slate-900">{supplier.name}</h1>
            <p className="mt-1 text-sm text-slate-500 font-mono">{supplier.code}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <ProcStatusBadge value={supplier.status} />
            {supplier.prequalified && (
              <span className="rounded-full bg-emerald-100 px-3 py-0.5 text-xs font-semibold text-emerald-800">
                Prequalified{supplier.approvedDate ? ` — ${new Date(supplier.approvedDate).toLocaleDateString("en-KE")}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={[
              "px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors",
              activeTab === t.id
                ? "border-emerald-700 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-900",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contacts tab */}
      {activeTab === "contacts" && (
        <ProcSection title="Supplier Contacts">
          <div className="space-y-4">
            {/* Existing contacts */}
            {(contacts ?? []).length === 0 && (
              <p className="text-sm text-slate-400">No contacts added yet.</p>
            )}
            {(contacts ?? []).map((c: SupplierContact) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border bg-white p-3">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{c.fullName} {c.primary && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Primary</span>}</p>
                  {c.role && <p className="text-xs text-slate-500">{c.role}</p>}
                  <p className="text-xs text-slate-400">{c.phone} {c.email && `· ${c.email}`}</p>
                </div>
                <button onClick={() => deleteContact(c.id)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Add contact form */}
            {contactErr && <p className="text-sm text-red-600">{contactErr}</p>}
            <div className="rounded-xl border bg-slate-50 p-4 space-y-3">
              <p className="text-xs font-bold uppercase text-slate-500">Add Contact</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <input placeholder="Full Name *" value={contactForm.fullName} onChange={e => setContactForm(f => ({...f, fullName: e.target.value}))}
                  className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                <input placeholder="Role / Title" value={contactForm.role ?? ""} onChange={e => setContactForm(f => ({...f, role: e.target.value}))}
                  className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                <input placeholder="Phone" value={contactForm.phone ?? ""} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))}
                  className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                <input placeholder="Email" type="email" value={contactForm.email ?? ""} onChange={e => setContactForm(f => ({...f, email: e.target.value}))}
                  className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={contactForm.primary ?? false}
                  onChange={e => setContactForm(f => ({...f, primary: e.target.checked}))}
                  className="rounded" />
                Primary Contact
              </label>
              <button onClick={saveContact} disabled={savingContact || !contactForm.fullName?.trim()}
                className="flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50">
                <Plus className="h-4 w-4" />
                {savingContact ? "Saving…" : "Add Contact"}
              </button>
            </div>
          </div>
        </ProcSection>
      )}

      {/* Documents tab */}
      {activeTab === "documents" && (
        <ProcSection title="Compliance Documents">
          <div className="space-y-4">
            {(documents ?? []).length === 0 && (
              <p className="text-sm text-slate-400">No documents uploaded yet.</p>
            )}
            {(documents ?? []).map(d => (
              <div key={d.id} className="flex items-center justify-between rounded-xl border bg-white p-3">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{d.fileName}</p>
                  <p className="text-xs text-slate-500">{d.documentType?.replace(/_/g, " ")} {d.expiryDate ? `· Expires: ${new Date(d.expiryDate).toLocaleDateString("en-KE")}` : ""}</p>
                  {d.uploadedAt && <p className="text-xs text-slate-400">Uploaded: {new Date(d.uploadedAt).toLocaleDateString("en-KE")}</p>}
                </div>
              </div>
            ))}

            {/* Upload form */}
            {docErr && <p className="text-sm text-red-600">{docErr}</p>}
            <div className="rounded-xl border bg-slate-50 p-4 space-y-3">
              <p className="text-xs font-bold uppercase text-slate-500">Upload Document</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Document Type</label>
                  <select value={docType} onChange={e => setDocType(e.target.value)}
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400">
                    {DOC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Expiry Date (optional)</label>
                  <input type="date" value={docExpiry} onChange={e => setDocExpiry(e.target.value)}
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                </div>
              </div>
              <input ref={fileRef} type="file" className="hidden"
                onChange={e => { if (e.target.files?.[0]) uploadDoc(e.target.files[0]); }} />
              <button onClick={() => fileRef.current?.click()} disabled={uploadingDoc}
                className="flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50">
                <Upload className="h-4 w-4" />
                {uploadingDoc ? "Uploading…" : "Choose & Upload File"}
              </button>
            </div>
          </div>
        </ProcSection>
      )}

      {/* Tab panels */}
      {activeTab === "details" && (
        <ProcSection title="Supplier Details">
          <ProcGrid cols={3}>
            <ProcField label="Code" value={supplier.code} />
            <ProcField label="Category" value={supplier.category} />
            <ProcField label="Contact Person" value={supplier.contactPerson} />
            <ProcField label="Phone" value={supplier.phone} />
            <ProcField label="Email" value={supplier.email} />
            <ProcField label="KRA PIN" value={supplier.kraPin} />
            <ProcField label="VAT Cert No" value={supplier.vatCertNo} />
            <ProcField label="Address" value={supplier.address} span2 />
          </ProcGrid>
          {(activateErr || suspendErr) && (
            <p className="mt-3 text-sm text-red-600">{activateErr ?? suspendErr}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            {supplier.status !== "ACTIVE" && (
              <button type="button" onClick={() => doActivate()} disabled={activating}
                className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
                {activating ? "Activating…" : "Activate Supplier"}
              </button>
            )}
            {supplier.status === "ACTIVE" && (
              <button type="button" onClick={() => doSuspend()} disabled={suspending}
                className="rounded-xl border border-amber-400 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-60">
                {suspending ? "Suspending…" : "Suspend Supplier"}
              </button>
            )}
          </div>
        </ProcSection>
      )}

      {activeTab === "prequalification" && (
        <ProcSection title="Prequalification Records">
          {supplierPrequals.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              No prequalification records found for this supplier.
              <div className="mt-3">
                <Link
                  to="/procurement/supplier-prequal/new"
                  className="text-emerald-700 font-semibold hover:underline"
                >
                  Create Prequalification
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Serial No</th>
                    <th className="px-4 py-3">Eval Date</th>
                    <th className="px-4 py-3 text-right">Tech Score</th>
                    <th className="px-4 py-3 text-right">Pass Mark</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {supplierPrequals.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900">{p.serialNo}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {p.date ? new Date(p.date).toLocaleDateString("en-KE") : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-bold">{p.techTotal}</td>
                      <td className="px-4 py-3 text-right text-slate-500">{p.passMark}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/procurement/supplier-prequal/${p.id}`}
                          className="font-semibold text-emerald-700 hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ProcSection>
      )}

      {activeTab === "evaluations" && (
        <ProcSection title="Supplier Evaluations">
          {supplierEvals.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              No evaluation records found for this supplier.
              <div className="mt-3">
                <Link
                  to="/procurement/supplier-eval/new"
                  className="text-emerald-700 font-semibold hover:underline"
                >
                  Create Evaluation
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Serial No</th>
                    <th className="px-4 py-3">Eval Date</th>
                    <th className="px-4 py-3">Commodity / Service</th>
                    <th className="px-4 py-3">Recommendation</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {supplierEvals.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900">{e.serialNo}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {e.date ? new Date(e.date).toLocaleDateString("en-KE") : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{e.commodityService}</td>
                      <td className="px-4 py-3">
                        <ProcStatusBadge value={e.recommendation} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/procurement/supplier-eval/${e.id}`}
                          className="font-semibold text-emerald-700 hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ProcSection>
      )}
    </div>
  );
}
